/**
 * @fileoverview Main entry point for the Storyteller web application.
 * This component initializes the Storyteller environment and handles the main flow of the application.
 *
 * @example
 * // Basic usage
 * <App language="en" assignmentId="123" token="abc" onLanguageChange={() => Promise.resolve()} />
 */

import { start } from '@novoic/storyteller'
import { useEffect, useRef, useState } from 'react'
import { Footer } from './Footer'
import { Header } from './Header'

/**
 * @interface AppProps
 * @description Props for the App component.
 * @property {string} language - The language of the application.
 * @property {string} assignmentId - The ID of the assignment.
 * @property {string} token - The token for the assignment.
 * @property {Function} onLanguageChange - A function to handle language change events.
 */
interface AppProps {
    language: string
    assignmentId?: string
    token?: string
    onLanguageChange: (language: string) => Promise<void>
}

/**
 * Bootstrap function that fetches the token and assignment id from the host.
 * @returns {Promise<{token: string, assignment_id: string, language: string}>}
 */
async function bootstrap(): Promise<Novoic.BootstrapResponse> {
    const response = await fetch(`${process.env.ASSIGN_HOST}${location.pathname}${location.search}`)
    const data = await response.json()

    if (data.code === 404) {
        throw new Error()
    }

    return data
}

/**
 * Main application component that initializes the Storyteller environment and handles the main flow of the application.
 * @param {AppProps} props - The props for the App component.
 * @returns {JSX.Element} The App component.
 */
export function App({ assignmentId, language, token, onLanguageChange }: AppProps) {
    const ref = useRef<HTMLDivElement>(null)

    const [bootstrapData, setBootstrapData] = useState<Partial<Novoic.BootstrapResponse>>({
        token,
        assignment_id: assignmentId,
        language,
    })

    const [assessments, setAssessments] = useState<Novoic.Assessment[]>([])
    const [isInitialised, setIsInitialised] = useState(false)

    function onProgress({ assessments }: { assessments: Novoic.Assessment[] }) {
        setAssessments(assessments)
    }

    function onLoad({
        assessments,
        assignment,
    }: {
        assessments: Novoic.Assessment[]
        assignment: Novoic.Assignment
    }) {
        ref.current?.classList.add('storyteller-container')
        if (assignment) {
            setBootstrapData({ ...bootstrapData, battery_id: assignment.battery_id })
        }
        if (assessments) {
            setAssessments(assessments)
        }
    }

    useEffect(() => {
        const { token, assignment_id, language } = bootstrapData
        if (!isInitialised && !(token && assignment_id && language)) {
            bootstrap()
                .then(({ token, assignment_id, language }) => {
                    setBootstrapData({ token, assignment_id, language })
                })
                .catch(() => {
                    start(ref.current, {
                        language,
                        standalone: true,
                    })
                    setIsInitialised(true)
                })
        }
    }, [assignmentId])

    useEffect(() => {
        const { token, assignment_id, language } = bootstrapData
        const params = new URLSearchParams(location.search)
        const debug = params.get('debug')

        let redirectUrl = `${
            location.pathname
        }?assignment_id=${assignment_id}&language=${language}&token=${token}&nocache=${Date.now()}`

        if (debug) {
            redirectUrl += `&debug=${debug}`
        }

        if (!isInitialised && ref.current && token && assignment_id && language) {
            start(ref.current, {
                token,
                assignmentId: assignment_id,
                language,
                standalone: true,
                onLoad,
                onLanguageChange,
                onProgress,
            })
            setIsInitialised(true)
            history.replaceState({}, '', redirectUrl)
        }
    }, [
        isInitialised,
        ref.current,
        bootstrapData.token,
        bootstrapData.assignment_id,
        bootstrapData.language,
    ])

    return (
        <div className="container">
            {assessments && (
                <Header batteryId={bootstrapData.battery_id} assessments={assessments} />
            )}

            <div id="storyteller-host" ref={ref}></div>

            {assessments && <Footer />}
        </div>
    )
}
